import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    TextField,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    SelectChangeEvent,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    useMediaQuery,
    Stack
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Add as AddIcon, Delete as DeleteIcon, AttachFile as AttachFileIcon, Search as SearchIcon } from '@mui/icons-material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { useSnackbar } from 'notistack';
import { IFormularioAjuste, IItemAjuste } from '../../interfaces/AjusteInventario';
import { IUbicacion } from '../../interfaces/Ubicacion';
import ajusteInventarioService from '../../services/ajusteInventarioService';
import ubicacionService from '../../services/ubicacionService';
import productoService from '../../services/productoService';
import api from '../../services/api';
import * as XLSX from 'xlsx';

const initialFormulario: IFormularioAjuste = {
    nroFormulario: 0,
    tipoCliente: null,
    nroTipoFormulario: null,
    fechaEmision: new Date(),
    horaEmision: new Date(),
    descripcion: '',
    nroUbicacionOrigen: null,
    detUbicacionOrigen: null,
    nroUbicacionDestino: null,
    detUbicacionDestino: null,
    usuario: null,
    integrar: 1,
    CodigoRamoProv: '',
    letra: '',
    nroSuc: Number(localStorage.getItem('nroSuc') || localStorage.getItem('sucursal') || 0) || 0,
    nroComprobante: 0
};

const initialItem: IItemAjuste = {
    nroIDFormulario: 0,
    nroItem: 1,
    fecha: new Date(),
    codigoArticulo: '',
    descripcion: '',
    cantidad: 0,
    costo: 0,
    precioUnitario: 0,
    descuento: 0,
    subtotal: 0,
    nroUbicacion: null,
    nroUbicacionDestino: null,
    notas: ''
};

const AjusteInventarioForm: React.FC = () => {
    const navigate = useNavigate();
    const { nroFormulario } = useParams();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [tipo, setTipo] = useState(location.state?.tipo || 'transferencia');
    const { enqueueSnackbar } = useSnackbar();

    const [formulario, setFormulario] = useState<IFormularioAjuste>(initialFormulario);
    const [items, setItems] = useState<IItemAjuste[]>([{ ...initialItem }]);
    const [loading, setLoading] = useState(false);
    const [ubicaciones, setUbicaciones] = useState<IUbicacion[]>([]);
    const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
    const [tipoAjuste, setTipoAjuste] = useState<'positivo' | 'negativo' | 'transferencia'>(() => {
        if (location.state?.tipo) {
            return location.state.tipo;
        }
        return 'positivo'; // valor por defecto
    });
    const fileInputRef = React.useRef<HTMLInputElement | null>(null);
    const [openImportar, setOpenImportar] = useState(false);
    const [sucursales, setSucursales] = useState<Array<{ nroSuc: number; nombreSucursal: string }>>([]);
    const [loadingSucursales, setLoadingSucursales] = useState(false);
    const [openProductoSearch, setOpenProductoSearch] = useState(false);
    const [productoSearchIndex, setProductoSearchIndex] = useState<number | null>(null);
    const [productos, setProductos] = useState<any[]>([]);
    const [productoSearchTerm, setProductoSearchTerm] = useState('');
    const [productoSearchLoading, setProductoSearchLoading] = useState(false);

    const getNextNumber = useCallback(async () => {
        try {
            const nextNumber = await ajusteInventarioService.getNextNroFormulario(tipo);
            setFormulario(prev => ({ ...prev, nroFormulario: nextNumber }));
        } catch (error) {
            enqueueSnackbar('Error al obtener el siguiente número', { variant: 'error' });
        }
    }, [tipo, enqueueSnackbar]);

    const loadUbicaciones = useCallback(async () => {
        try {
            const data = await ubicacionService.getUbicaciones();
            setUbicaciones(data);
        } catch (error) {
            console.error('Error al cargar ubicaciones:', error);
            enqueueSnackbar('Error al cargar las ubicaciones', { variant: 'error' });
        }
    }, [enqueueSnackbar]);

    const loadAjuste = useCallback(async (numero: number) => {
        try {
            setLoading(true);
            const data = await ajusteInventarioService.getAjuste(numero);
            
            if (data.nroTipoFormulario) {
                switch (data.nroTipoFormulario) {
                    case "11":
                        setTipo('positivo');
                        break;
                    case "12":
                        setTipo('negativo');
                        break;
                    case "13":
                        setTipo('transferencia');
                        break;
                }
            }

            setFormulario({
                nroFormulario: data.nroFormulario,
                tipoCliente: data.tipoCliente,
                nroTipoFormulario: data.nroTipoFormulario,
                fechaEmision: data.fechaEmision,
                horaEmision: data.horaEmision,
                descripcion: data.descripcion,
                nroUbicacionOrigen: data.nroUbicacionOrigen,
                detUbicacionOrigen: data.detUbicacionOrigen,
                nroUbicacionDestino: data.nroUbicacionDestino,
                detUbicacionDestino: data.detUbicacionDestino,
                usuario: data.usuario,
                integrar: data.integrar,
                CodigoRamoProv: data.CodigoRamoProv || '',
                letra: data.letra || '',
                nroSuc: data.nroSuc || Number(localStorage.getItem('nroSuc') || localStorage.getItem('sucursal') || 0) || 0,
                nroComprobante: data.nroComprobante || 0
            });
            setItems(data.items || []);
        } catch (error) {
            console.error('Error al cargar el ajuste:', error);
            enqueueSnackbar('Error al cargar el ajuste', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar]);

    useEffect(() => {
        if (nroFormulario) {
            loadAjuste(Number(nroFormulario));
        } else {
            // Para nuevo ajuste, inicializar con fecha actual
            setFormulario(prev => ({
                ...prev,
                fechaEmision: new Date()
            }));
            getNextNumber();
        }
        loadUbicaciones();
    }, [nroFormulario, getNextNumber, loadAjuste, loadUbicaciones]);

    // Cargar sucursales desde la API
    useEffect(() => {
        const cargarSucursales = async () => {
            try {
                setLoadingSucursales(true);
                const response = await api.get('/api/SucursalesVenta');
                const data = response.data;
                
                // La respuesta tiene la estructura { items: [...] }
                if (data && Array.isArray(data.items)) {
                    setSucursales(data.items);
                } else if (Array.isArray(data)) {
                    setSucursales(data);
                } else {
                    setSucursales([]);
                }
            } catch (error) {
                console.error('Error al cargar sucursales:', error);
                enqueueSnackbar('Error al cargar las sucursales', { variant: 'error' });
                setSucursales([]);
            } finally {
                setLoadingSucursales(false);
            }
        };
        
        cargarSucursales();
    }, [enqueueSnackbar]);

    // Persistir sucursal válida en localStorage
    useEffect(() => {
        const v = Number(formulario.nroSuc);
        if (Number.isFinite(v) && v > 0) {
            try {
                localStorage.setItem('nroSuc', String(v));
                localStorage.setItem('sucursal', String(v));
            } catch {}
        }
    }, [formulario.nroSuc]);

    useEffect(() => {
        if (tipoAjuste === 'transferencia') return;

        if (formulario.nroUbicacionOrigen && formulario.nroUbicacionDestino) {
            setTipoAjuste('transferencia');
        } else if (formulario.nroUbicacionOrigen && !formulario.nroUbicacionDestino) {
            setTipoAjuste('negativo');
        } else if (!formulario.nroUbicacionOrigen && formulario.nroUbicacionDestino) {
            setTipoAjuste('positivo');
        }
    }, [formulario.nroUbicacionOrigen, formulario.nroUbicacionDestino, tipoAjuste]);

    const normalizeCode = (code: any): string => {
        const raw = String(code ?? '').trim();
        if (raw === '') return '';
        // Si es numérico, normalizar quitando ceros a la izquierda
        if (/^\d+$/.test(raw)) {
            return String(Number(raw));
        }
        return raw.toUpperCase();
    };

    const handleTextFieldChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        if (name) {
            setFormulario(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSelectChange = (e: SelectChangeEvent<number>) => {
        const { name, value } = e.target;
        if (name) {
            setFormulario(prev => {
                const newFormulario = { ...prev, [name]: value };
                
                // Si cambia la ubicación origen, actualizar su descripción
                if (name === 'nroUbicacionOrigen') {
                    const ubicacion = ubicaciones.find(u => u.nroUbicacion === value);
                    newFormulario.detUbicacionOrigen = ubicacion?.nombreUbicacion || null;
                }
                
                // Si cambia la ubicación destino, actualizar su descripción
                if (name === 'nroUbicacionDestino') {
                    const ubicacion = ubicaciones.find(u => u.nroUbicacion === value);
                    newFormulario.detUbicacionDestino = ubicacion?.nombreUbicacion || null;
                }
                
                return newFormulario;
            });
        }
    };

    const handleSucursalChange = (e: SelectChangeEvent<number | string>) => {
        const value = e.target.value;
        setFormulario(prev => ({
            ...prev,
            nroSuc: Number(value) || 0
        }));
    };

    const searchProduct = async (codigo: string, index: number) => {
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        const timeoutId = setTimeout(async () => {
            if (codigo) {
                try {
                    const producto = await productoService.getProducto(codigo);
                    setItems(prevItems => {
                        const newItems = [...prevItems];
                        newItems[index] = {
                            ...newItems[index],
                            descripcion: producto.descripcion,
                            // Asegurar que también se actualiza el costo cuando se selecciona/busca un producto
                            costo: (producto as any).costo ?? 0
                        };
                        return newItems;
                    });
                } catch (error) {
                    console.error('Error al buscar el producto:', error);
                    enqueueSnackbar(`El código de artículo "${codigo}" no existe`, { variant: 'error' });
                    setItems(prevItems => {
                        const newItems = [...prevItems];
                        newItems[index] = {
                            ...newItems[index],
                            descripcion: '',
                            costo: 0
                        };
                        return newItems;
                    });
                }
            }
        }, 500);

        setSearchTimeout(timeoutId);
    };

    // Valida en backend que los códigos existan. Devuelve la lista de códigos inexistentes
    const validateCodesExist = async (rawCodes: string[]): Promise<string[]> => {
        const unique = Array.from(new Set(rawCodes.map(c => normalizeCode(c)).filter(c => c)));
        if (unique.length === 0) return [];
        const results = await Promise.allSettled(unique.map(c => productoService.getProducto(c)));
        const missing: string[] = [];
        results.forEach((res, idx) => {
            if (res.status === 'rejected') missing.push(unique[idx]);
        });
        return missing;
    };

    const handleItemChange = (index: number, e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
        const { name, value } = e.target;
        if (name) {
            const newItems = [...items];
            newItems[index] = { ...newItems[index], [name]: value };
            setItems(newItems);

            if (name === 'codigoArticulo') {
                searchProduct(value as string, index);
            }
        }
    };

    const handleOpenProductoSearch = (index: number) => {
        setProductoSearchIndex(index);
        setProductoSearchTerm('');
        setProductos([]);
        setOpenProductoSearch(true);
    };

    const handleProductoSearch = async () => {
        try {
            setProductoSearchLoading(true);
            const response = await productoService.getProductos({
                searchTerm: productoSearchTerm,
                pageNumber: 1,
                pageSize: 50
            });
            setProductos(response.items);
        } catch (err: any) {
            enqueueSnackbar('Error al buscar productos', { variant: 'error' });
        } finally {
            setProductoSearchLoading(false);
        }
    };

    const handleProductoSelect = async (producto: any) => {
        if (productoSearchIndex !== null) {
            try {
                const codigoArticulo = producto.codigoArticulo || producto.codigo;
                
                // Obtener el producto completo para obtener el costo
                const productoCompleto = await productoService.getProducto(codigoArticulo);
                
                const newItems = [...items];
                newItems[productoSearchIndex] = {
                    ...newItems[productoSearchIndex],
                    codigoArticulo: codigoArticulo,
                    descripcion: productoCompleto.descripcion || producto.descripcion || producto.nombreLargo || '',
                    costo: productoCompleto.costo || producto.costo || 0
                };
                setItems(newItems);
            } catch (error) {
                console.error('Error al obtener el producto completo:', error);
                // Si falla, al menos actualizar con los datos básicos
                const newItems = [...items];
                newItems[productoSearchIndex] = {
                    ...newItems[productoSearchIndex],
                    codigoArticulo: producto.codigoArticulo || producto.codigo,
                    descripcion: producto.descripcion || producto.nombreLargo || ''
                };
                setItems(newItems);
                enqueueSnackbar('Error al obtener el costo del producto', { variant: 'warning' });
            }
        }
        setOpenProductoSearch(false);
        setProductoSearchIndex(null);
        setProductoSearchTerm('');
        setProductos([]);
    };

    const addItem = () => {
        setItems(prev => [...prev, { ...initialItem, nroItem: prev.length + 1 }]);
    };

    const removeItem = (index: number) => {
        setItems(prev => prev.filter((_, i) => i !== index));
    };

    const handleImportChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<boolean> => {
        const file = e.target.files?.[0];
        if (!file) return false;
        try {
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data);
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const rows: any[] = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

            // Primera pasada: parse y validaciones básicas
            type ParsedRow = {
                nroItem: number;
                codigo: string;
                cantidadNum: number;
                costoNum: number;
                precioUnitarioNum: number;
                descuentoNum: number;
                subtotalNum: number;
                nroUbicacionVal: number | null;
                nroUbicacionDestinoVal: number | null;
                fecha: Date;
                descripcion: string;
                notas: string;
            };

            const parsedRows: ParsedRow[] = [];
            const invalids = { invalidNumericCount: 0, missingCodeCount: 0, invalidDateCount: 0, invalidNroItemCount: 0 };
            const invalidNumericColumns = new Set<string>();
            const invalidDateColumns = new Set<string>();

            const isProvided = (v: any) => v !== undefined && v !== null && String(v).trim() !== '';
            const toNumberOrInvalid = (v: any): { valid: boolean; value: number } => {
                if (!isProvided(v)) return { valid: true, value: 0 };
                const n = Number(v);
                return { valid: !isNaN(n), value: isNaN(n) ? 0 : n };
            };

            for (let idx = 0; idx < rows.length; idx++) {
                const r = rows[idx];
                const normalized: Record<string, any> = {};
                Object.keys(r).forEach(k => normalized[String(k).toLowerCase().trim()] = r[k]);
                const get = (key: string, alts: string[] = []) => {
                    const keys = [key.toLowerCase(), ...alts.map(a => a.toLowerCase())];
                    for (const k of keys) { if (normalized[k] !== undefined) return normalized[k]; }
                    return undefined;
                };

                const codigo = normalizeCode(get('codigoArticulo', ['codigo', 'codigoproducto']));
                if (!codigo) { invalids.missingCodeCount++; continue; }

                const nroItemParsed = toNumberOrInvalid(get('nroitem'));
                const nroItem = nroItemParsed.value;
                if (!nroItemParsed.valid || !Number.isFinite(nroItem)) { invalids.invalidNroItemCount++; continue; }

                const cantidadRaw = get('cantidad');
                const costoRaw = get('costo');
                const precioUnitarioRaw = get('preciounitario');
                const descuentoRaw = get('descuento');
                const subtotalRaw = get('subtotal');
                const nroUbicacionRaw = get('nroubicacion');
                const nroUbicacionDestinoRaw = get('nroubicaciondestino');

                const cantidadNum = toNumberOrInvalid(cantidadRaw);
                const costoNum = toNumberOrInvalid(costoRaw);
                const precioUnitarioNum = toNumberOrInvalid(precioUnitarioRaw);
                const descuentoNum = toNumberOrInvalid(descuentoRaw);
                const subtotalNum = toNumberOrInvalid(subtotalRaw);
                const nroUbicacionNum = toNumberOrInvalid(nroUbicacionRaw);
                const nroUbicacionDestinoNum = toNumberOrInvalid(nroUbicacionDestinoRaw);

                const hasNumericError = !cantidadNum.valid || !costoNum.valid || !precioUnitarioNum.valid || !descuentoNum.valid || !subtotalNum.valid || !nroUbicacionNum.valid || !nroUbicacionDestinoNum.valid;
                if (hasNumericError) {
                    if (!cantidadNum.valid) invalidNumericColumns.add('cantidad');
                    if (!costoNum.valid) invalidNumericColumns.add('costo');
                    if (!precioUnitarioNum.valid) invalidNumericColumns.add('precioUnitario');
                    if (!descuentoNum.valid) invalidNumericColumns.add('descuento');
                    if (!subtotalNum.valid) invalidNumericColumns.add('subtotal');
                    if (!nroUbicacionNum.valid) invalidNumericColumns.add('nroUbicacion');
                    if (!nroUbicacionDestinoNum.valid) invalidNumericColumns.add('nroUbicacionDestino');
                    invalids.invalidNumericCount++;
                    continue;
                }

                const fechaRaw = get('fecha');
                const fechaCandidate = fechaRaw ? new Date(fechaRaw) : new Date();
                const fecha = isNaN(fechaCandidate.getTime()) ? null : fechaCandidate;
                if (!fecha) { invalids.invalidDateCount++; invalidDateColumns.add('fecha'); continue; }

                // Determinar ubicaciones según el tipo de ajuste
                let nroUbicacionVal: number | null;
                let nroUbicacionDestinoVal: number | null;
                
                if (isProvided(get('nroubicacion'))) {
                    // Si está en el Excel, usar esa
                    nroUbicacionVal = nroUbicacionNum.value;
                } else {
                    // Si no está en el Excel, usar la del formulario o asignar según el tipo
                    if (tipoAjuste === 'negativo' || tipoAjuste === 'transferencia') {
                        nroUbicacionVal = formulario.nroUbicacionOrigen ?? null;
                    } else {
                        nroUbicacionVal = null;
                    }
                }
                
                if (isProvided(get('nroubicaciondestino'))) {
                    // Si está en el Excel, usar esa
                    nroUbicacionDestinoVal = nroUbicacionDestinoNum.value;
                } else {
                    // Si no está en el Excel, usar la del formulario o asignar según el tipo
                    if (tipoAjuste === 'positivo' || tipoAjuste === 'transferencia') {
                        nroUbicacionDestinoVal = formulario.nroUbicacionDestino ?? null;
                    } else {
                        nroUbicacionDestinoVal = null;
                    }
                }
                
                parsedRows.push({
                    nroItem,
                    codigo,
                    cantidadNum: cantidadNum.value,
                    costoNum: costoNum.value,
                    precioUnitarioNum: precioUnitarioNum.value,
                    descuentoNum: descuentoNum.value,
                    subtotalNum: subtotalNum.value,
                    nroUbicacionVal,
                    nroUbicacionDestinoVal,
                    fecha,
                    descripcion: (get('descripcion') || '').toString(),
                    notas: (get('notas', ['observaciones']))?.toString() ?? ''
                });
            }

            // Duplicados por nroItem: contra existentes (desde backend si hay nroFormulario) y dentro del archivo
            let existingNroItems = new Set<number>();
            try {
                if (nroFormulario) {
                    const ajusteActual = await ajusteInventarioService.getAjuste(Number(nroFormulario));
                    existingNroItems = new Set<number>((ajusteActual.items || []).map((i: any) => Number(i.nroItem)).filter((n: number) => Number.isFinite(n)) as number[]);
                } else {
                    existingNroItems = new Set<number>(items.map(i => Number(i.nroItem)).filter(n => Number.isFinite(n)) as number[]);
                }
            } catch (fetchErr) {
                // Si falla la consulta, al menos validar contra los items en memoria
                existingNroItems = new Set<number>(items.map(i => Number(i.nroItem)).filter(n => Number.isFinite(n)) as number[]);
            }
            const countMap = new Map<number, number>();
            const duplicatedInFile: number[] = [];
            const duplicatedAgainstExisting: number[] = [];
            for (const pr of parsedRows) {
                countMap.set(pr.nroItem, (countMap.get(pr.nroItem) || 0) + 1);
            }
            countMap.forEach((cnt, n) => {
                if (cnt > 1) duplicatedInFile.push(n);
            });
            for (const pr of parsedRows) {
                if (existingNroItems.has(pr.nroItem)) duplicatedAgainstExisting.push(pr.nroItem);
            }

            if (duplicatedInFile.length || duplicatedAgainstExisting.length) {
                // Reasignación automática de nroItem para permitir múltiples filas
                let maxExisting = 0;
                existingNroItems.forEach(n => { if (n > maxExisting) maxExisting = n; });
                parsedRows.forEach(pr => { if (pr.nroItem > maxExisting) maxExisting = pr.nroItem; });
                const newNumbers: Array<{ old: number, neu: number }> = [];
                parsedRows.forEach(pr => {
                    const isDupInFile = (countMap.get(pr.nroItem) || 0) > 1;
                    const isDupExisting = existingNroItems.has(pr.nroItem);
                    if (isDupInFile || isDupExisting) {
                        maxExisting += 1;
                        newNumbers.push({ old: pr.nroItem, neu: maxExisting });
                        pr.nroItem = maxExisting;
                    }
                });
                if (newNumbers.length) {
                    const sample = newNumbers.slice(0, 5).map(p => `${p.old}→${p.neu}`).join(', ');
                    enqueueSnackbar(`Se reasignaron ${newNumbers.length} nroItem(s) para evitar duplicados (${sample}${newNumbers.length > 5 ? '…' : ''}).`, { variant: 'warning', autoHideDuration: 9000 });
                }
            }

            // Duplicados por codigoArticulo (normalizado)
            let existingCodes = new Set<string>();
            try {
                if (nroFormulario) {
                    const ajusteActual = await ajusteInventarioService.getAjuste(Number(nroFormulario));
                    existingCodes = new Set<string>((ajusteActual.items || []).map((i: any) => normalizeCode(i.codigoArticulo)).filter((c: string) => c !== ''));
                } else {
                    existingCodes = new Set<string>(items.map(i => normalizeCode(i.codigoArticulo)).filter(c => c !== ''));
                }
            } catch {
                existingCodes = new Set<string>(items.map(i => normalizeCode(i.codigoArticulo)).filter(c => c !== ''));
            }

            const codeCountMap = new Map<string, number>();
            const duplicatedCodesInFile: string[] = [];
            const duplicatedCodesAgainstExisting: string[] = [];
            parsedRows.forEach(pr => {
                codeCountMap.set(pr.codigo, (codeCountMap.get(pr.codigo) || 0) + 1);
            });
            codeCountMap.forEach((cnt, code) => { if (cnt > 1) duplicatedCodesInFile.push(code); });
            parsedRows.forEach(pr => { if (existingCodes.has(pr.codigo)) duplicatedCodesAgainstExisting.push(pr.codigo); });

            if (duplicatedCodesInFile.length || duplicatedCodesAgainstExisting.length) {
                // Mantenemos bloqueo por código para preservar integridad
                const parts: string[] = [];
                if (duplicatedCodesInFile.length) {
                    const list = duplicatedCodesInFile.slice(0, 5).join(', ');
                    parts.push(`códigos duplicados en archivo: ${list}${duplicatedCodesInFile.length > 5 ? '…' : ''}`);
                }
                if (duplicatedCodesAgainstExisting.length) {
                    const list = duplicatedCodesAgainstExisting.slice(0, 5).join(', ');
                    parts.push(`códigos ya existentes: ${list}${duplicatedCodesAgainstExisting.length > 5 ? '…' : ''}`);
                }
                enqueueSnackbar(`No se importó por duplicados de codigoArticulo (${parts.join(' | ')}).`, { variant: 'error', autoHideDuration: 9000 });
                return false;
            }

            // Validación extra: asegurar que los códigos existen en backend
            try {
                const missingCodes = await validateCodesExist(parsedRows.map(pr => pr.codigo));
                if (missingCodes.length) {
                    const sample = missingCodes.slice(0, 6).join(', ');
                    enqueueSnackbar(`No se importó: hay código(s) inexistente(s): ${sample}${missingCodes.length > 6 ? '…' : ''}`, { variant: 'error', autoHideDuration: 9000 });
                    return false;
                }
            } catch (err) {
                // Si la validación falla por red, no bloquear, pero avisar
                console.error('Error validando códigos', err);
            }

            // Construcción final de items a importar
            const imported: IItemAjuste[] = parsedRows.map(pr => ({
                nroIDFormulario: formulario.nroFormulario || 0,
                nroItem: pr.nroItem,
                fecha: pr.fecha,
                codigoArticulo: pr.codigo,
                descripcion: pr.descripcion,
                cantidad: pr.cantidadNum,
                costo: pr.costoNum,
                precioUnitario: pr.precioUnitarioNum,
                descuento: pr.descuentoNum,
                subtotal: pr.subtotalNum,
                nroUbicacion: pr.nroUbicacionVal,
                nroUbicacionDestino: pr.nroUbicacionDestinoVal,
                notas: pr.notas
            }));

            if (!imported.length && (invalids.invalidNumericCount || invalids.missingCodeCount || invalids.invalidDateCount || invalids.invalidNroItemCount)) {
                const msgs: string[] = [];
                if (invalids.missingCodeCount) msgs.push(`faltó codigoArticulo en ${invalids.missingCodeCount} fila(s)`);
                if (invalids.invalidNumericCount) {
                    const cols = Array.from(invalidNumericColumns).join(', ');
                    msgs.push(`${invalids.invalidNumericCount} fila(s) con números inválidos (columnas: ${cols})`);
                }
                if (invalids.invalidDateCount) {
                    const cols = Array.from(invalidDateColumns).join(', ');
                    msgs.push(`${invalids.invalidDateCount} fila(s) con fecha inválida (columnas: ${cols})`);
                }
                if (invalids.invalidNroItemCount) msgs.push(`${invalids.invalidNroItemCount} fila(s) con nroItem inválido`);
                enqueueSnackbar(`No se importaron filas: ${msgs.join(' | ')}`, { variant: 'error', autoHideDuration: 8000 });
                return false;
            }

            const isEmptyItem = (it: IItemAjuste) => {
                const codigo = String(it.codigoArticulo || '').trim();
                const descripcion = String(it.descripcion || '').trim();
                const notas = String(it.notas || '').trim();
                const cantidad = Number(it.cantidad || 0);
                const costo = Number(it.costo || 0);
                return codigo === '' && descripcion === '' && notas === '' && cantidad === 0 && costo === 0;
            };
            const cleanedExisting = items.filter(it => !isEmptyItem(it));
            const mergedItems = [...cleanedExisting, ...imported];

            // Persistir en backend si estamos editando un ajuste existente
            if (nroFormulario) {
                try {
                    // Determinar tipo para la API, igual que en handleSubmit
                    // Priorizar el tipo del estado inicial si está disponible
                    let tipoAjusteParaGuardar: string;
                    if (location.state?.tipo) {
                        tipoAjusteParaGuardar = location.state.tipo;
                    } else if (formulario.nroUbicacionOrigen && formulario.nroUbicacionDestino) {
                        tipoAjusteParaGuardar = 'transferencia';
                    } else if (formulario.nroUbicacionOrigen && !formulario.nroUbicacionDestino) {
                        tipoAjusteParaGuardar = 'negativo';
                    } else if (!formulario.nroUbicacionOrigen && formulario.nroUbicacionDestino) {
                        tipoAjusteParaGuardar = 'positivo';
                    } else {
                        throw new Error('Configuración de ubicaciones inválida');
                    }

                    // IMPORTANTE: TODOS los ajustes usan nroTipoFormulario = '11'
                    // El tipo se define por las ubicaciones
                    formulario.tipoCliente = 'A';
                    formulario.nroTipoFormulario = '11';
                    
                    // Asegurar que las ubicaciones estén configuradas correctamente según el tipo
                    if (tipoAjusteParaGuardar === 'positivo') {
                        formulario.nroUbicacionOrigen = 0;
                        if (!formulario.nroUbicacionDestino) {
                            throw new Error('Ajuste positivo requiere ubicación destino');
                        }
                    } else if (tipoAjusteParaGuardar === 'negativo') {
                        formulario.nroUbicacionDestino = 0;
                        if (!formulario.nroUbicacionOrigen) {
                            throw new Error('Ajuste negativo requiere ubicación origen');
                        }
                    } else if (tipoAjusteParaGuardar === 'transferencia') {
                        if (!formulario.nroUbicacionOrigen || !formulario.nroUbicacionDestino) {
                            throw new Error('Transferencia requiere ambas ubicaciones');
                        }
                    }

                    await ajusteInventarioService.updateAjuste(Number(nroFormulario), formulario, mergedItems, tipoAjusteParaGuardar);
                    setItems(mergedItems);
                    enqueueSnackbar(`Importados ${imported.length} ítems y guardados en el ajuste #${nroFormulario}`, { variant: 'success' });
                } catch (saveErr) {
                    console.error('Error guardando ítems importados:', saveErr);
                    enqueueSnackbar('Error al guardar los ítems importados en el backend', { variant: 'error' });
                    return false;
                }
            } else {
                // Nuevo ajuste: solo en memoria; se guardará al presionar Guardar
                // Asegurar que las ubicaciones se configuren según el tipo de ajuste si no están configuradas
                // Esto es importante para que los items importados tengan las ubicaciones correctas
                if (tipoAjuste === 'positivo' && !formulario.nroUbicacionDestino && imported.length > 0) {
                    // Si no hay ubicación destino configurada, usar la del primer item importado si existe
                    const primeraUbicacionDestino = imported.find(i => i.nroUbicacionDestino)?.nroUbicacionDestino;
                    if (primeraUbicacionDestino) {
                        setFormulario(prev => ({
                            ...prev,
                            nroUbicacionDestino: primeraUbicacionDestino,
                            detUbicacionDestino: ubicaciones.find(u => u.nroUbicacion === primeraUbicacionDestino)?.nombreUbicacion || null
                        }));
                    }
                } else if (tipoAjuste === 'negativo' && !formulario.nroUbicacionOrigen && imported.length > 0) {
                    // Si no hay ubicación origen configurada, usar la del primer item importado si existe
                    const primeraUbicacionOrigen = imported.find(i => i.nroUbicacion)?.nroUbicacion;
                    if (primeraUbicacionOrigen) {
                        setFormulario(prev => ({
                            ...prev,
                            nroUbicacionOrigen: primeraUbicacionOrigen,
                            detUbicacionOrigen: ubicaciones.find(u => u.nroUbicacion === primeraUbicacionOrigen)?.nombreUbicacion || null
                        }));
                    }
                } else if (tipoAjuste === 'transferencia') {
                    // Para transferencias, asegurar que ambas ubicaciones estén configuradas
                    const primeraUbicacionOrigen = imported.find(i => i.nroUbicacion)?.nroUbicacion;
                    const primeraUbicacionDestino = imported.find(i => i.nroUbicacionDestino)?.nroUbicacionDestino;
                    if (primeraUbicacionOrigen && !formulario.nroUbicacionOrigen) {
                        setFormulario(prev => ({
                            ...prev,
                            nroUbicacionOrigen: primeraUbicacionOrigen,
                            detUbicacionOrigen: ubicaciones.find(u => u.nroUbicacion === primeraUbicacionOrigen)?.nombreUbicacion || null
                        }));
                    }
                    if (primeraUbicacionDestino && !formulario.nroUbicacionDestino) {
                        setFormulario(prev => ({
                            ...prev,
                            nroUbicacionDestino: primeraUbicacionDestino,
                            detUbicacionDestino: ubicaciones.find(u => u.nroUbicacion === primeraUbicacionDestino)?.nombreUbicacion || null
                        }));
                    }
                }
                
                setItems(mergedItems);
                enqueueSnackbar(`Importados ${imported.length} ítems. Se guardarán al presionar Guardar.`, { variant: 'success' });
            }

            if (invalids.invalidNumericCount) enqueueSnackbar(`Omitidas ${invalids.invalidNumericCount} filas con columnas numéricas inválidas`, { variant: 'error' });
            if (invalids.missingCodeCount) enqueueSnackbar(`Omitidas ${invalids.missingCodeCount} filas sin codigoArticulo`, { variant: 'error' });
            if (invalids.invalidDateCount) enqueueSnackbar(`Omitidas ${invalids.invalidDateCount} filas con fecha inválida`, { variant: 'error' });
            if (invalids.invalidNroItemCount) enqueueSnackbar(`Omitidas ${invalids.invalidNroItemCount} filas con nroItem inválido`, { variant: 'error' });
            return true;
        } catch (error) {
            console.error('Error al importar Excel:', error);
            enqueueSnackbar('No se pudo leer el archivo Excel', { variant: 'error' });
            return false;
        } finally {
            e.target.value = '';
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setLoading(true);
            
            // ========== VALIDACIONES COMPLETAS ANTES DE GUARDAR ==========
            
            // 1. Validar que haya al menos un item
            if (!items || items.length === 0) {
                enqueueSnackbar('Error: Debe agregar al menos un ítem antes de guardar', { variant: 'error', autoHideDuration: 5000 });
                setLoading(false);
                return;
            }

            // 2. Validar datos obligatorios del formulario
            const erroresFormulario: string[] = [];
            if (!formulario.fechaEmision) {
                erroresFormulario.push('fecha de emisión');
            }
            if (!formulario.nroSuc) {
                erroresFormulario.push('número de sucursal');
            }
            
            if (erroresFormulario.length > 0) {
                enqueueSnackbar(`Error: Faltan datos obligatorios: ${erroresFormulario.join(', ')}`, { variant: 'error', autoHideDuration: 5000 });
                setLoading(false);
                return;
            }

            // 3. Validar datos obligatorios y tipos de dato en items
            const erroresItems: string[] = [];
            const tiposErroneos: string[] = [];
            const seenNroItem = new Set<number>();
            const seenCodigoArticulo = new Set<string>();
            
            items.forEach((item, index) => {
                const itemNum = index + 1;
                
                // Validar nroItem (obligatorio y único)
                const nroItem = Number(item.nroItem);
                if (!Number.isFinite(nroItem) || nroItem <= 0) {
                    erroresItems.push(`Item ${itemNum}: nroItem es obligatorio y debe ser un número mayor a 0`);
                } else if (seenNroItem.has(nroItem)) {
                    erroresItems.push(`Item ${itemNum}: nroItem "${nroItem}" está duplicado`);
                } else {
                    seenNroItem.add(nroItem);
                }
                
                // Validar codigoArticulo (obligatorio y único)
                const codigo = (item.codigoArticulo || '').trim().toUpperCase();
                if (!codigo) {
                    erroresItems.push(`Item ${itemNum}: código de artículo es obligatorio`);
                } else if (seenCodigoArticulo.has(codigo)) {
                    erroresItems.push(`Item ${itemNum}: código de artículo "${codigo}" está duplicado`);
                } else {
                    seenCodigoArticulo.add(codigo);
                }
                
                // Validar cantidad (obligatorio, numérico, > 0)
                const cantidad = Number(item.cantidad);
                if (!Number.isFinite(cantidad) || cantidad <= 0) {
                    tiposErroneos.push(`Item ${itemNum}: cantidad debe ser un número mayor a 0`);
                }
                
                // Validar costo (obligatorio, numérico, >= 0)
                const costo = Number(item.costo);
                if (!Number.isFinite(costo) || costo < 0) {
                    tiposErroneos.push(`Item ${itemNum}: costo debe ser un número mayor o igual a 0`);
                }
                
                // Validar fecha (obligatorio y válida)
                if (!item.fecha) {
                    erroresItems.push(`Item ${itemNum}: fecha es obligatoria`);
                } else {
                    const fecha = item.fecha instanceof Date ? item.fecha : new Date(item.fecha);
                    if (isNaN(fecha.getTime())) {
                        tiposErroneos.push(`Item ${itemNum}: fecha inválida`);
                    }
                }
            });
            
            // Mostrar errores de datos obligatorios
            if (erroresItems.length > 0) {
                const mensaje = erroresItems.slice(0, 3).join('; ') + (erroresItems.length > 3 ? ` ... y ${erroresItems.length - 3} más` : '');
                enqueueSnackbar(`Error: ${mensaje}`, { variant: 'error', autoHideDuration: 8000 });
                setLoading(false);
                return;
            }
            
            // Mostrar errores de tipos de dato
            if (tiposErroneos.length > 0) {
                const mensaje = tiposErroneos.slice(0, 3).join('; ') + (tiposErroneos.length > 3 ? ` ... y ${tiposErroneos.length - 3} más` : '');
                enqueueSnackbar(`Error de tipo de dato: ${mensaje}`, { variant: 'error', autoHideDuration: 8000 });
                setLoading(false);
                return;
            }

            // 4. Validar duplicados contra el backend (si estamos editando un ajuste existente)
            const erroresDuplicadosBackend: string[] = [];
            if (nroFormulario) {
                try {
                    const ajusteExistente = await ajusteInventarioService.getAjuste(Number(nroFormulario));
                    if (ajusteExistente?.items) {
                        const nroItemsExistentes = new Set(ajusteExistente.items.map((i: any) => Number(i.nroItem)));
                        const codigosExistentes = new Set(ajusteExistente.items.map((i: any) => (i.codigoArticulo || '').trim().toUpperCase()));
                        
                        // Validar contra items existentes (excluyendo los que estamos editando)
                        items.forEach((item, index) => {
                            const itemNum = index + 1;
                            const nroItem = Number(item.nroItem);
                            const codigo = (item.codigoArticulo || '').trim().toUpperCase();
                            
                            // Verificar si el nroItem ya existe en el backend (y no es el mismo item que estamos editando)
                            if (nroItemsExistentes.has(nroItem)) {
                                // Verificar si este item ya existe en el ajuste actual
                                const itemExistente = ajusteExistente.items.find((i: any) => Number(i.nroItem) === nroItem);
                                if (itemExistente && itemExistente.nroItem !== item.nroItem) {
                                    erroresDuplicadosBackend.push(`Item ${itemNum}: nroItem "${nroItem}" ya existe en este ajuste`);
                                }
                            }
                            
                            // Verificar si el código ya existe en el backend
                            if (codigosExistentes.has(codigo)) {
                                const itemExistente = ajusteExistente.items.find((i: any) => (i.codigoArticulo || '').trim().toUpperCase() === codigo);
                                if (itemExistente && itemExistente.codigoArticulo !== item.codigoArticulo) {
                                    erroresDuplicadosBackend.push(`Item ${itemNum}: código de artículo "${codigo}" ya existe en este ajuste`);
                                }
                            }
                        });
                        
                        // Si hay errores de duplicados contra el backend, mostrarlos
                        if (erroresDuplicadosBackend.length > 0) {
                            const mensaje = erroresDuplicadosBackend.slice(0, 3).join('; ') + (erroresDuplicadosBackend.length > 3 ? ` ... y ${erroresDuplicadosBackend.length - 3} más` : '');
                            enqueueSnackbar(`Error: ${mensaje}`, { variant: 'error', autoHideDuration: 8000 });
                            setLoading(false);
                            return;
                        }
                    }
                } catch (err) {
                    console.error('Error validando duplicados contra el backend', err);
                    // No bloquear el guardado si hay error al validar contra el backend
                }
            }

            // 5. Validar existencia de códigos de artículo
            try {
                const codigosArticulos = items.map(i => i.codigoArticulo).filter(c => c && c.trim());
                if (codigosArticulos.length > 0) {
                    const missingCodes = await validateCodesExist(codigosArticulos);
                    if (missingCodes.length) {
                        const sample = missingCodes.slice(0, 5).join(', ');
                        enqueueSnackbar(`Error: Código(s) de artículo inexistente(s): ${sample}${missingCodes.length > 5 ? ` ... y ${missingCodes.length - 5} más` : ''}`, { variant: 'error', autoHideDuration: 8000 });
                        setLoading(false);
                        return;
                    }
                }
            } catch (err) {
                console.error('Error validando códigos antes de guardar', err);
                enqueueSnackbar('Error al validar los códigos de artículo', { variant: 'error' });
                setLoading(false);
                return;
            }
            
            // 6. Determinar el tipo basado en el estado inicial (prioridad) o en las ubicaciones
            let tipoAjuste: string;
            // Priorizar el tipo del estado inicial si está disponible
            if (location.state?.tipo) {
                tipoAjuste = location.state.tipo;
                console.log('Usando tipo del estado inicial:', tipoAjuste);
            } else if (formulario.nroUbicacionOrigen && formulario.nroUbicacionDestino) {
                tipoAjuste = 'transferencia';
            } else if (formulario.nroUbicacionOrigen && !formulario.nroUbicacionDestino) {
                tipoAjuste = 'negativo';
            } else if (!formulario.nroUbicacionOrigen && formulario.nroUbicacionDestino) {
                tipoAjuste = 'positivo';
            } else {
                enqueueSnackbar('Error: Configure las ubicaciones según el tipo de ajuste', { variant: 'error' });
                setLoading(false);
                return;
            }
            
            console.log('Tipo de ajuste determinado:', tipoAjuste, 'ubicaciones:', {
                origen: formulario.nroUbicacionOrigen,
                destino: formulario.nroUbicacionDestino
            });

            // 7. Validar que las ubicaciones estén configuradas según el tipo
            if (tipoAjuste === 'negativo' && !formulario.nroUbicacionOrigen) {
                enqueueSnackbar('Error: Los ajustes negativos requieren una ubicación origen', { variant: 'error', autoHideDuration: 5000 });
                setLoading(false);
                return;
            }
            if (tipoAjuste === 'positivo' && !formulario.nroUbicacionDestino) {
                enqueueSnackbar('Error: Los ajustes positivos requieren una ubicación destino', { variant: 'error', autoHideDuration: 5000 });
                setLoading(false);
                return;
            }
            if (tipoAjuste === 'transferencia' && (!formulario.nroUbicacionOrigen || !formulario.nroUbicacionDestino)) {
                enqueueSnackbar('Error: Las transferencias requieren ubicación origen y destino', { variant: 'error', autoHideDuration: 5000 });
                setLoading(false);
                return;
            }

            // IMPORTANTE: TODOS los ajustes usan nroTipoFormulario = '11'
            // El tipo se define por las ubicaciones:
            // - Positivo: nroUbicacionOrigen = 0
            // - Negativo: nroUbicacionDestino = 0
            // - Transferencia: ambos != 0
            formulario.tipoCliente = 'A';
            formulario.nroTipoFormulario = '11';

            // Preparar items con formato correcto para el backend (fechas en ISO string)
            // IMPORTANTE: El tipo se define por las ubicaciones:
            // - Positivo: nroUbicacion = 0, nroUbicacionDestino != 0
            // - Negativo: nroUbicacion != 0, nroUbicacionDestino = 0
            // - Transferencia: ambos != 0
            const itemsFormateados = items.map(item => {
                let nroUbicacionItem: number = 0;
                let nroUbicacionDestinoItem: number = 0;
                
                if (tipoAjuste === 'positivo') {
                    // Ajuste positivo: nroUbicacion = 0, nroUbicacionDestino != 0
                    nroUbicacionItem = 0;
                    nroUbicacionDestinoItem = item.nroUbicacionDestino 
                        ? Number(item.nroUbicacionDestino) 
                        : (formulario.nroUbicacionDestino ? Number(formulario.nroUbicacionDestino) : 0);
                } else if (tipoAjuste === 'negativo') {
                    // Ajuste negativo: nroUbicacion != 0, nroUbicacionDestino = 0
                    nroUbicacionItem = item.nroUbicacion 
                        ? Number(item.nroUbicacion) 
                        : (formulario.nroUbicacionOrigen ? Number(formulario.nroUbicacionOrigen) : 0);
                    nroUbicacionDestinoItem = 0; // DEBE ser 0 para ajustes negativos
                } else if (tipoAjuste === 'transferencia') {
                    // Transferencia: ambos != 0
                    nroUbicacionItem = item.nroUbicacion 
                        ? Number(item.nroUbicacion) 
                        : (formulario.nroUbicacionOrigen ? Number(formulario.nroUbicacionOrigen) : 0);
                    nroUbicacionDestinoItem = item.nroUbicacionDestino 
                        ? Number(item.nroUbicacionDestino) 
                        : (formulario.nroUbicacionDestino ? Number(formulario.nroUbicacionDestino) : 0);
                }
                
                return {
                    ...item,
                    fecha: item.fecha instanceof Date ? item.fecha.toISOString() : (typeof item.fecha === 'string' ? item.fecha : new Date().toISOString()),
                    cantidad: Number(item.cantidad) || 0,
                    costo: Number(item.costo) || 0,
                    precioUnitario: Number(item.precioUnitario) || 0,
                    descuento: Number(item.descuento) || 0,
                    subtotal: Number(item.subtotal) || 0,
                    nroItem: Number(item.nroItem) || 0,
                    nroIDFormulario: formulario.nroFormulario || 0,
                    nroUbicacion: nroUbicacionItem,
                    nroUbicacionDestino: nroUbicacionDestinoItem
                };
            });

            // Preparar formulario con formato correcto para el backend
            // IMPORTANTE: El tipo se define por las ubicaciones:
            // - Positivo: nroUbicacionOrigen = 0, nroUbicacionDestino != 0
            // - Negativo: nroUbicacionOrigen != 0, nroUbicacionDestino = 0
            // - Transferencia: ambos != 0
            let nroUbicacionOrigenFinal: number = 0;
            let nroUbicacionDestinoFinal: number = 0;
            
            if (tipoAjuste === 'positivo') {
                // Ajuste positivo: nroUbicacionOrigen = 0, nroUbicacionDestino != 0
                nroUbicacionOrigenFinal = 0;
                nroUbicacionDestinoFinal = formulario.nroUbicacionDestino ? Number(formulario.nroUbicacionDestino) : 0;
            } else if (tipoAjuste === 'negativo') {
                // Ajuste negativo: nroUbicacionOrigen != 0, nroUbicacionDestino = 0
                nroUbicacionOrigenFinal = formulario.nroUbicacionOrigen ? Number(formulario.nroUbicacionOrigen) : 0;
                nroUbicacionDestinoFinal = 0; // DEBE ser 0 para ajustes negativos
            } else if (tipoAjuste === 'transferencia') {
                // Transferencia: ambos != 0
                nroUbicacionOrigenFinal = formulario.nroUbicacionOrigen ? Number(formulario.nroUbicacionOrigen) : 0;
                nroUbicacionDestinoFinal = formulario.nroUbicacionDestino ? Number(formulario.nroUbicacionDestino) : 0;
            }
            
            // Formatear el formulario para el backend
            const formularioFormateado = {
                ...formulario,
                fechaEmision: formulario.fechaEmision instanceof Date ? formulario.fechaEmision.toISOString() : (typeof formulario.fechaEmision === 'string' ? formulario.fechaEmision : new Date().toISOString()),
                horaEmision: formulario.horaEmision instanceof Date ? formulario.horaEmision.toISOString() : (typeof formulario.horaEmision === 'string' ? formulario.horaEmision : new Date().toISOString()),
                nroUbicacionOrigen: nroUbicacionOrigenFinal,
                nroUbicacionDestino: nroUbicacionDestinoFinal
            };
            
            console.log('💾💾💾 GUARDANDO AJUSTE - VERIFICACIÓN COMPLETA:', {
                tipoAjuste,
                nroTipoFormulario: formularioFormateado.nroTipoFormulario,
                nroUbicacionOrigen: formularioFormateado.nroUbicacionOrigen,
                nroUbicacionDestino: formularioFormateado.nroUbicacionDestino,
                formularioCompleto: formularioFormateado,
                itemsCompletos: itemsFormateados.map(item => ({
                    codigoArticulo: item.codigoArticulo,
                    nroUbicacion: item.nroUbicacion,
                    nroUbicacionDestino: item.nroUbicacionDestino
                }))
            });
            
            // VERIFICACIÓN CRÍTICA: Validar que las ubicaciones estén correctas según el tipo
            if (tipoAjuste === 'negativo') {
                if (formularioFormateado.nroUbicacionDestino !== 0) {
                    console.error('❌❌❌ ERROR CRÍTICO: Ajuste negativo con nroUbicacionDestino != 0:', formularioFormateado.nroUbicacionDestino);
                    enqueueSnackbar('Error: Para ajustes negativos, nroUbicacionDestino debe ser 0', { variant: 'error' });
                    setLoading(false);
                    return;
                }
                if (formularioFormateado.nroUbicacionOrigen === 0) {
                    console.error('❌❌❌ ERROR CRÍTICO: Ajuste negativo con nroUbicacionOrigen == 0');
                    enqueueSnackbar('Error: Para ajustes negativos, nroUbicacionOrigen debe ser diferente de 0', { variant: 'error' });
                    setLoading(false);
                    return;
                }
                console.log('✅✅✅ VERIFICACIÓN: Ajuste negativo correcto (origen != 0, destino = 0)');
            } else if (tipoAjuste === 'positivo') {
                if (formularioFormateado.nroUbicacionOrigen !== 0) {
                    console.error('❌❌❌ ERROR CRÍTICO: Ajuste positivo con nroUbicacionOrigen != 0:', formularioFormateado.nroUbicacionOrigen);
                    enqueueSnackbar('Error: Para ajustes positivos, nroUbicacionOrigen debe ser 0', { variant: 'error' });
                    setLoading(false);
                    return;
                }
                if (formularioFormateado.nroUbicacionDestino === 0) {
                    console.error('❌❌❌ ERROR CRÍTICO: Ajuste positivo con nroUbicacionDestino == 0');
                    enqueueSnackbar('Error: Para ajustes positivos, nroUbicacionDestino debe ser diferente de 0', { variant: 'error' });
                    setLoading(false);
                    return;
                }
                console.log('✅✅✅ VERIFICACIÓN: Ajuste positivo correcto (origen = 0, destino != 0)');
            } else if (tipoAjuste === 'transferencia') {
                if (formularioFormateado.nroUbicacionOrigen === 0 || formularioFormateado.nroUbicacionDestino === 0) {
                    console.error('❌❌❌ ERROR CRÍTICO: Transferencia con ubicación == 0');
                    enqueueSnackbar('Error: Para transferencias, ambas ubicaciones deben ser diferentes de 0', { variant: 'error' });
                    setLoading(false);
                    return;
                }
                console.log('✅✅✅ VERIFICACIÓN: Transferencia correcta (origen != 0, destino != 0)');
            }

            // Primero guardamos el ajuste (operación principal)
            if (nroFormulario) {
                await ajusteInventarioService.updateAjuste(Number(nroFormulario), formularioFormateado as any, itemsFormateados as any, tipoAjuste);
            } else {
                // Crear nuevo ajuste - asegurar que todos los campos estén correctos
                console.log('🔵 Creando nuevo ajuste - Datos a enviar:', { 
                    tipoAjuste, 
                    tipoCliente: formularioFormateado.tipoCliente, 
                    nroTipoFormulario: formularioFormateado.nroTipoFormulario, 
                    itemsCount: itemsFormateados.length,
                    nroUbicacionOrigen: formularioFormateado.nroUbicacionOrigen,
                    nroUbicacionDestino: formularioFormateado.nroUbicacionDestino,
                    formulario: formularioFormateado,
                    items: itemsFormateados,
                    primerItemUbicaciones: itemsFormateados.length > 0 ? {
                        nroUbicacion: itemsFormateados[0].nroUbicacion,
                        nroUbicacionDestino: itemsFormateados[0].nroUbicacionDestino
                    } : null
                });
                try {
                    const response = await ajusteInventarioService.createAjuste(formularioFormateado as any, itemsFormateados as any, tipoAjuste);
                    console.log('✅ Respuesta del createAjuste:', response);
                    // Si el backend devuelve el nroFormulario, actualizarlo para que quede registrado
                    let nroFormularioGuardado = formulario.nroFormulario;
                    if (response?.nroFormulario) {
                        nroFormularioGuardado = response.nroFormulario;
                        formulario.nroFormulario = response.nroFormulario;
                        console.log('✅ Ajuste creado exitosamente con nroFormulario:', response.nroFormulario);
                    } else if (response?.formulario?.nroFormulario) {
                        nroFormularioGuardado = response.formulario.nroFormulario;
                        formulario.nroFormulario = response.formulario.nroFormulario;
                        console.log('✅ Ajuste creado exitosamente con nroFormulario:', response.formulario.nroFormulario);
                    } else if (formulario.nroFormulario) {
                        nroFormularioGuardado = formulario.nroFormulario;
                        console.log('✅ Usando nroFormulario del formulario:', formulario.nroFormulario);
                    } else {
                        console.warn('⚠️ El backend no devolvió nroFormulario en la respuesta:', response);
                    }
                    
                    // Guardar el nroFormulario para pasarlo al navigate
                    (formularioFormateado as any).nroFormularioFinal = nroFormularioGuardado;
                } catch (createError: any) {
                    console.error('❌ Error al crear el ajuste:', createError);
                    console.error('❌ Detalles del error:', {
                        status: createError?.response?.status,
                        statusText: createError?.response?.statusText,
                        data: createError?.response?.data,
                        message: createError?.message
                    });
                    throw createError; // Re-lanzar para que se maneje en el catch general
                }
            }

            // Operaciones secundarias: actualizar cantidades y registrar movimientos
            const sideEffectErrors: string[] = [];
            for (const item of itemsFormateados) {
                // actualizar cantidades (no debe romper el flujo si falla)
                try {
                    await productoService.actualizarCantidad(
                        item.codigoArticulo,
                        Number(item.cantidad) || 0,
                        tipoAjuste,
                        formulario.nroUbicacionOrigen || undefined,
                        formulario.nroUbicacionDestino || undefined
                    );
                } catch (err) {
                    console.error('Error actualizarCantidad', err);
                    sideEffectErrors.push(`No se pudo actualizar stock de ${item.codigoArticulo}`);
                }

                // registrar movimientos históricos
                if (tipoAjuste === 'transferencia') {
                    if (!formulario.nroUbicacionOrigen || !formulario.nroUbicacionDestino) {
                        sideEffectErrors.push('Faltan ubicaciones para registrar movimientos de transferencia');
                        continue;
                    }
                    try {
                        await ajusteInventarioService.registrarMovStockHistorico({
                            idformulario: formulario.nroFormulario,
                            codigoArticulo: item.codigoArticulo,
                            tipoAjuste: 'transferencia',
                            cantidad: Number(item.cantidad) || 0,
                            deposito: formulario.nroUbicacionOrigen,
                            nroSuc: formulario.nroSuc || 1,
                            esEgreso: true,
                            nroItems: item.nroItem,
                            letra: formulario.letra || '',
                            nroComprobante: formulario.nroComprobante || 0
                        });
                        await ajusteInventarioService.registrarMovStockHistorico({
                            idformulario: formulario.nroFormulario,
                            codigoArticulo: item.codigoArticulo,
                            tipoAjuste: 'transferencia',
                            cantidad: Number(item.cantidad) || 0,
                            deposito: formulario.nroUbicacionDestino,
                            nroSuc: formulario.nroSuc || 1,
                            esEgreso: false,
                            nroItems: item.nroItem,
                            letra: formulario.letra || '',
                            nroComprobante: formulario.nroComprobante || 0
                        });
                    } catch (err) {
                        console.error('Error registrarMovStockHistorico transferencia', err);
                        sideEffectErrors.push(`No se pudo registrar movimientos de ${item.codigoArticulo}`);
                    }
                } else {
                    const deposito = tipoAjuste === 'positivo' 
                        ? formulario.nroUbicacionDestino 
                        : formulario.nroUbicacionOrigen;
                    if (!deposito) {
                        sideEffectErrors.push(`Falta ubicación para registrar movimiento ${tipoAjuste}`);
                        continue;
                    }
                    try {
                        await ajusteInventarioService.registrarMovStockHistorico({
                            idformulario: formulario.nroFormulario,
                            codigoArticulo: item.codigoArticulo,
                            tipoAjuste: tipoAjuste,
                            cantidad: Number(item.cantidad) || 0,
                            deposito: deposito,
                            nroSuc: formulario.nroSuc || 1,
                            esEgreso: tipoAjuste === 'negativo',
                            nroItems: item.nroItem,
                            letra: formulario.letra || '',
                            nroComprobante: formulario.nroComprobante || 0
                        });
                    } catch (err) {
                        console.error('Error registrarMovStockHistorico', err);
                        sideEffectErrors.push(`No se pudo registrar movimiento de ${item.codigoArticulo}`);
                    }
                }
            }

            // Verificar que el ajuste se guardó correctamente (solo para nuevos ajustes)
            if (!nroFormulario && !formulario.nroFormulario) {
                console.warn('⚠️ No se recibió nroFormulario del backend, pero continuando de todas formas');
                // No bloquear el flujo, el backend puede haber guardado el ajuste de todas formas
            }

            if (sideEffectErrors.length) {
                enqueueSnackbar(`Ajuste guardado con observaciones: ${sideEffectErrors.slice(0,3).join(' | ')}${sideEffectErrors.length>3?'…':''}`, { variant: 'warning' });
            } else {
                enqueueSnackbar('Ajuste guardado exitosamente', { variant: 'success' });
            }
            
            // Obtener el número de formulario del ajuste guardado
            let nroFormularioGuardado = nroFormulario || formulario.nroFormulario || 0;
            
            // Si es un nuevo ajuste, intentar obtener el nroFormulario de la respuesta
            if (!nroFormulario && formularioFormateado) {
                const nroFormularioFinal = (formularioFormateado as any).nroFormularioFinal;
                if (nroFormularioFinal) {
                    nroFormularioGuardado = nroFormularioFinal;
                }
            }
            
            console.log('✅✅✅ AJUSTE GUARDADO EXITOSAMENTE');
            console.log('✅ Nro. Formulario:', nroFormularioGuardado);
            console.log('✅ Tipo de ajuste:', tipoAjuste);
            console.log('✅ Ubicaciones guardadas:', {
                nroUbicacionOrigen: formularioFormateado.nroUbicacionOrigen,
                nroUbicacionDestino: formularioFormateado.nroUbicacionDestino
            });
            
            // Navegar a la lista de transferencias y forzar recarga
            // IMPORTANTE: Usar replace: false para que se pueda volver atrás
            // Pasar el nroFormularioGuardado y tipoAjusteGuardado para que se pueda buscar y mostrar el ajuste
            console.log('🚀 Navegando a /inventario/transferencias con state:', {
                refresh: Date.now(),
                nroFormularioGuardado: nroFormularioGuardado,
                tipoAjusteGuardado: tipoAjuste
            });
            navigate('/inventario/transferencias', { 
                replace: false, 
                state: { 
                    refresh: Date.now(),
                    nroFormularioGuardado: nroFormularioGuardado,
                    tipoAjusteGuardado: tipoAjuste
                } 
            });
        } catch (error: any) {
            console.error('❌ Error al guardar el ajuste:', error);
            let backendMsg = '';
            try {
                backendMsg = error?.response?.data?.message || error?.response?.data?.detail || error?.response?.data?.title || '';
                if (!backendMsg && error?.response?.data && typeof error.response.data === 'object') {
                    backendMsg = JSON.stringify(error.response.data);
                }
            } catch {}
            const status = error?.response?.status;
            const statusText = error?.response?.statusText;
            let msg = backendMsg || error?.message || 'Error desconocido';
            if (status) {
                msg = `Error ${status}${statusText ? ` (${statusText})` : ''}: ${msg}`;
            }
            enqueueSnackbar('Error al guardar el ajuste: ' + msg, { variant: 'error', autoHideDuration: 10000 });
            console.error('❌ Detalles completos del error:', {
                status,
                statusText,
                data: error?.response?.data,
                message: error?.message,
                stack: error?.stack
            });
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateReport = async () => {
        if (!nroFormulario) return;
        
        try {
            setLoading(true);
            const response = await ajusteInventarioService.getReporte(Number(nroFormulario));
            const blob = new Blob([response], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');
        } catch (error) {
            console.error('Error al generar el reporte:', error);
            enqueueSnackbar('Error al generar el reporte', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleDescargarPlantillaItems = () => {
        try {
            const headers = [
                'nroIDFormulario',
                'nroItem',
                'fecha',
                'codigoArticulo',
                'descripcion',
                'cantidad',
                'costo',
                'precioUnitario',
                'descuento',
                'subtotal',
                'nroUbicacion',
                'nroUbicacionDestino',
                'notas'
            ];
            
            // Determinar el nombre del archivo según el tipo de ajuste
            let nombreArchivo = 'PlantillaItems';
            if (tipoAjuste === 'positivo') {
                nombreArchivo = 'PlantillaItemsAjustePositivo.xlsx';
            } else if (tipoAjuste === 'negativo') {
                nombreArchivo = 'PlantillaItemsAjusteNegativo.xlsx';
            } else if (tipoAjuste === 'transferencia') {
                nombreArchivo = 'PlantillaItemsTransferencia.xlsx';
            } else {
                nombreArchivo = 'PlantillaItemsAjuste.xlsx';
            }
            
            const data = [headers, new Array(headers.length).fill('')];
            const ws = XLSX.utils.aoa_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Items');
            XLSX.writeFile(wb, nombreArchivo);
            enqueueSnackbar('Plantilla Excel descargada', { variant: 'success', autoHideDuration: 3000 });
        } catch (error) {
            enqueueSnackbar('Error al crear la plantilla Excel', { variant: 'error', autoHideDuration: 4000 });
        }
    };

    const handleUbicacionOrigenChange = (event: SelectChangeEvent<number>) => {
        const value = event.target.value as number;
        setFormulario(prev => ({
            ...prev,
            nroUbicacionOrigen: value,
            detUbicacionOrigen: ubicaciones.find(u => u.nroUbicacion === value)?.nombreUbicacion || null
        }));
    };

    const handleUbicacionDestinoChange = (event: SelectChangeEvent<number>) => {
        const value = event.target.value as number;
        setFormulario(prev => ({
            ...prev,
            nroUbicacionDestino: value,
            detUbicacionDestino: ubicaciones.find(u => u.nroUbicacion === value)?.nombreUbicacion || null
        }));
    };

    return (
        <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', p: { xs: 2, sm: 3 } }}>
            <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', p: 3 }}>
                <Typography 
                    variant="h4" 
                    gutterBottom 
                    sx={{ 
                        fontSize: { xs: '1.5rem', sm: '2.125rem' },
                        fontWeight: 600,
                        color: '#1976d2',
                        mb: 3
                    }}
                >
                    {nroFormulario ? 'Editar Ajuste' : tipoAjuste === 'positivo' ? 'Nuevo Ajuste Positivo' : tipoAjuste === 'negativo' ? 'Nuevo Ajuste Negativo' : 'Nueva Transferencia'}
                </Typography>

                <form onSubmit={handleSubmit}>
                    <Card sx={{ mb: 2, borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                        <CardContent>
                            <Grid container spacing={2}>
                                {/* Primera fila - Información básica */}
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="Fecha"
                                        type="date"
                                        name="fechaEmision"
                                        value={formulario.fechaEmision ? new Date(formulario.fechaEmision).toLocaleDateString('en-CA') : ''}
                                        onChange={handleTextFieldChange}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                bgcolor: '#fafafa',
                                                '&:hover': {
                                                    bgcolor: '#f5f5f5',
                                                },
                                                '&.Mui-focused': {
                                                    bgcolor: '#ffffff',
                                                },
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <FormControl fullWidth required>
                                        <InputLabel>Nº Sucursal</InputLabel>
                                        <Select
                                            value={formulario.nroSuc && formulario.nroSuc > 0 ? formulario.nroSuc : ''}
                                            onChange={handleSucursalChange}
                                            label="Nº Sucursal"
                                            disabled={loadingSucursales}
                                            sx={{
                                                borderRadius: 2,
                                                bgcolor: '#fafafa',
                                                '&:hover': {
                                                    bgcolor: '#f5f5f5',
                                                },
                                                '&.Mui-focused': {
                                                    bgcolor: '#ffffff',
                                                },
                                            }}
                                        >
                                            {sucursales.map((sucursal) => (
                                                <MenuItem key={sucursal.nroSuc} value={sucursal.nroSuc}>
                                                    {sucursal.nroSuc} - {sucursal.nombreSucursal?.trim() || `Sucursal ${sucursal.nroSuc}`}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="Descripción"
                                        name="descripcion"
                                        value={formulario.descripcion || ''}
                                        onChange={handleTextFieldChange}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                bgcolor: '#fafafa',
                                                '&:hover': {
                                                    bgcolor: '#f5f5f5',
                                                },
                                                '&.Mui-focused': {
                                                    bgcolor: '#ffffff',
                                                },
                                            },
                                        }}
                                    />
                                </Grid>

                            {/* Tercera fila - Ubicaciones */}
                            {(tipoAjuste === 'negativo' || tipoAjuste === 'transferencia') && (
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Ubicación Origen</InputLabel>
                                        <Select
                                            value={formulario.nroUbicacionOrigen || ''}
                                            onChange={handleUbicacionOrigenChange}
                                            label="Ubicación Origen"
                                            sx={{
                                                borderRadius: 2,
                                                bgcolor: '#fafafa',
                                                '&:hover': {
                                                    bgcolor: '#f5f5f5',
                                                },
                                                '&.Mui-focused': {
                                                    bgcolor: '#ffffff',
                                                },
                                            }}
                                        >
                                            {ubicaciones.map((ubicacion) => (
                                                <MenuItem key={ubicacion.nroUbicacion} value={ubicacion.nroUbicacion}>
                                                    {ubicacion.nombreUbicacion}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}
                            {(tipoAjuste === 'positivo' || tipoAjuste === 'transferencia') && (
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Ubicación Destino</InputLabel>
                                        <Select
                                            value={formulario.nroUbicacionDestino || ''}
                                            onChange={handleUbicacionDestinoChange}
                                            label="Ubicación Destino"
                                            sx={{
                                                borderRadius: 2,
                                                bgcolor: '#fafafa',
                                                '&:hover': {
                                                    bgcolor: '#f5f5f5',
                                                },
                                                '&.Mui-focused': {
                                                    bgcolor: '#ffffff',
                                                },
                                            }}
                                        >
                                            {ubicaciones.map((ubicacion) => (
                                                <MenuItem key={ubicacion.nroUbicacion} value={ubicacion.nroUbicacion}>
                                                    {ubicacion.nombreUbicacion}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}
                        </Grid>
                    </CardContent>
                </Card>

                <Card sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                    <CardContent>
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between', 
                            alignItems: { xs: 'stretch', sm: 'center' },
                            mb: 2,
                            gap: { xs: 2, sm: 1 }
                        }}>
                            <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, fontWeight: 600, color: '#1976d2' }}>
                                Items
                            </Typography>
                            <Box sx={{ 
                                display: 'flex', 
                                flexDirection: { xs: 'column', sm: 'row' },
                                gap: 1,
                                width: { xs: '100%', sm: 'auto' }
                            }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<AttachFileIcon />}
                                    onClick={() => setOpenImportar(true)}
                                    fullWidth={isMobile}
                                    sx={{
                                        borderRadius: 2,
                                        px: 3,
                                        py: 1,
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                    }}
                                >
                                    {isMobile ? 'Importar' : 'Importar items'}
                                </Button>
                                <Button
                                    startIcon={<AddIcon />}
                                    onClick={addItem}
                                    variant="contained"
                                    color="primary"
                                    fullWidth={isMobile}
                                    sx={{
                                        borderRadius: 2,
                                        px: 3,
                                        py: 1,
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                    }}
                                >
                                    Agregar Item
                                </Button>
                            </Box>
                        </Box>

                        {isMobile ? (
                            <Stack spacing={2}>
                                {items.map((item, index) => (
                                    <Card key={index} variant="outlined" sx={{ borderRadius: 2, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                                        <CardContent>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                                <Typography variant="subtitle2" fontWeight="bold" color="#1976d2">
                                                    Item #{index + 1}
                                                </Typography>
                                                <IconButton
                                                    color="error"
                                                    onClick={() => removeItem(index)}
                                                    disabled={items.length === 1}
                                                    size="small"
                                                    sx={{
                                                        borderRadius: 2,
                                                        '&:hover': {
                                                            bgcolor: 'error.light',
                                                            color: 'white',
                                                        },
                                                    }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Box>
                                            <Stack spacing={2}>
                                                <TextField
                                                    label="Código"
                                                    name="codigoArticulo"
                                                    value={item.codigoArticulo}
                                                    onChange={(e: any) => handleItemChange(index, e)}
                                                    size="small"
                                                    fullWidth
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 2,
                                                            bgcolor: '#fafafa',
                                                            '&:hover': {
                                                                bgcolor: '#f5f5f5',
                                                            },
                                                            '&.Mui-focused': {
                                                                bgcolor: '#ffffff',
                                                            },
                                                        },
                                                    }}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleOpenProductoSearch(index)}
                                                                edge="end"
                                                            >
                                                                <SearchIcon />
                                                            </IconButton>
                                                        )
                                                    }}
                                                />
                                                <TextField
                                                    label="Descripción"
                                                    name="descripcion"
                                                    value={item.descripcion}
                                                    onChange={(e: any) => handleItemChange(index, e)}
                                                    size="small"
                                                    InputProps={{
                                                        readOnly: true
                                                    }}
                                                    fullWidth
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 2,
                                                            bgcolor: '#fafafa',
                                                        },
                                                    }}
                                                />
                                                <Grid container spacing={2}>
                                                    <Grid item xs={6}>
                                                        <TextField
                                                            label="Cantidad"
                                                            name="cantidad"
                                                            type="number"
                                                            value={item.cantidad}
                                                            onChange={(e: any) => handleItemChange(index, e)}
                                                            size="small"
                                                            fullWidth
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    borderRadius: 2,
                                                                    bgcolor: '#fafafa',
                                                                    '&:hover': {
                                                                        bgcolor: '#f5f5f5',
                                                                    },
                                                                    '&.Mui-focused': {
                                                                        bgcolor: '#ffffff',
                                                                    },
                                                                },
                                                            }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <TextField
                                                            label="Costo"
                                                            name="costo"
                                                            type="number"
                                                            value={item.costo}
                                                            onChange={(e: any) => handleItemChange(index, e)}
                                                            size="small"
                                                            fullWidth
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    borderRadius: 2,
                                                                    bgcolor: '#fafafa',
                                                                    '&:hover': {
                                                                        bgcolor: '#f5f5f5',
                                                                    },
                                                                    '&.Mui-focused': {
                                                                        bgcolor: '#ffffff',
                                                                    },
                                                                },
                                                            }}
                                                        />
                                                    </Grid>
                                                </Grid>
                                                <TextField
                                                    label="Notas"
                                                    name="notas"
                                                    value={item.notas}
                                                    onChange={(e: any) => handleItemChange(index, e)}
                                                    size="small"
                                                    fullWidth
                                                    multiline
                                                    rows={2}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 2,
                                                            bgcolor: '#fafafa',
                                                            '&:hover': {
                                                                bgcolor: '#f5f5f5',
                                                            },
                                                            '&.Mui-focused': {
                                                                bgcolor: '#ffffff',
                                                            },
                                                        },
                                                    }}
                                                />
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                ))}
                            </Stack>
                        ) : (
                            <TableContainer component={Paper} sx={{ overflowX: 'auto', borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                                <Table sx={{ minWidth: 650 }}>
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                            <TableCell width="15%" sx={{ fontWeight: 600 }}>Código</TableCell>
                                            <TableCell width="35%" sx={{ fontWeight: 600 }}>Descripción</TableCell>
                                            <TableCell width="10%" sx={{ fontWeight: 600 }}>Cantidad</TableCell>
                                            <TableCell width="10%" sx={{ fontWeight: 600 }}>Costo</TableCell>
                                            <TableCell width="20%" sx={{ fontWeight: 600 }}>Notas</TableCell>
                                            <TableCell width="10%" sx={{ fontWeight: 600 }}>Acciones</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {items.map((item, index) => (
                                            <TableRow 
                                                key={index}
                                                sx={{
                                                    '&:hover': {
                                                        bgcolor: '#f9f9f9',
                                                    },
                                                }}
                                            >
                                                <TableCell width="15%">
                                                    <TextField
                                                        name="codigoArticulo"
                                                        value={item.codigoArticulo}
                                                        onChange={(e: any) => handleItemChange(index, e)}
                                                        size="small"
                                                        fullWidth
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                borderRadius: 2,
                                                                bgcolor: '#fafafa',
                                                                '&:hover': {
                                                                    bgcolor: '#f5f5f5',
                                                                },
                                                                '&.Mui-focused': {
                                                                    bgcolor: '#ffffff',
                                                                },
                                                            },
                                                        }}
                                                        InputProps={{
                                                            endAdornment: (
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleOpenProductoSearch(index)}
                                                                    edge="end"
                                                                >
                                                                    <SearchIcon />
                                                                </IconButton>
                                                            )
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell width="35%">
                                                    <TextField
                                                        name="descripcion"
                                                        value={item.descripcion}
                                                        onChange={(e: any) => handleItemChange(index, e)}
                                                        size="small"
                                                        InputProps={{
                                                            readOnly: true
                                                        }}
                                                        fullWidth
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                borderRadius: 2,
                                                                bgcolor: '#fafafa',
                                                            },
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell width="10%">
                                                    <TextField
                                                        name="cantidad"
                                                        type="number"
                                                        value={item.cantidad}
                                                        onChange={(e: any) => handleItemChange(index, e)}
                                                        size="small"
                                                        fullWidth
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                borderRadius: 2,
                                                                bgcolor: '#fafafa',
                                                                '&:hover': {
                                                                    bgcolor: '#f5f5f5',
                                                                },
                                                                '&.Mui-focused': {
                                                                    bgcolor: '#ffffff',
                                                                },
                                                            },
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell width="10%">
                                                    <TextField
                                                        name="costo"
                                                        type="number"
                                                        value={item.costo}
                                                        onChange={(e: any) => handleItemChange(index, e)}
                                                        size="small"
                                                        fullWidth
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                borderRadius: 2,
                                                                bgcolor: '#fafafa',
                                                                '&:hover': {
                                                                    bgcolor: '#f5f5f5',
                                                                },
                                                                '&.Mui-focused': {
                                                                    bgcolor: '#ffffff',
                                                                },
                                                            },
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell width="20%">
                                                    <TextField
                                                        name="notas"
                                                        value={item.notas}
                                                        onChange={(e: any) => handleItemChange(index, e)}
                                                        size="small"
                                                        fullWidth
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                borderRadius: 2,
                                                                bgcolor: '#fafafa',
                                                                '&:hover': {
                                                                    bgcolor: '#f5f5f5',
                                                                },
                                                                '&.Mui-focused': {
                                                                    bgcolor: '#ffffff',
                                                                },
                                                            },
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell width="10%">
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => removeItem(index)}
                                                        disabled={items.length === 1}
                                                        sx={{
                                                            borderRadius: 2,
                                                            '&:hover': {
                                                                bgcolor: 'error.light',
                                                                color: 'white',
                                                            },
                                                        }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </CardContent>
                </Card>

                {/* Diálogo de importación (estructura similar a Importar Clientes) */}
                <Dialog 
                    open={openImportar} 
                    onClose={() => setOpenImportar(false)} 
                    maxWidth="sm" 
                    fullWidth
                    fullScreen={isMobile}
                >
                    <DialogTitle>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CloudUploadIcon sx={{ color: 'primary.main', fontSize: 30, mr: 1 }} />
                            <Typography variant="h6" fontWeight={700}>Importar Items</Typography>
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <Box>
                            {/* Bloque 1: Descargar plantilla */}
                            <Box sx={{ border: '2px dashed #ccc', borderRadius: 3, mb: 3, p: 3, background: '#fafbfd' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <CloudDownloadIcon color="primary" sx={{ fontSize: 30, mr: 1 }} />
                                    <Typography component="span" fontWeight={700} fontSize={18}>1. Descargar Plantilla</Typography>
                                </Box>
                                <Typography variant="body2" sx={{ mb: 2 }}>
                                    Descarga la plantilla Excel con el formato correcto para importar items.
                                </Typography>
                                <Button variant="contained" color="info" fullWidth sx={{ fontWeight: 700, py: 1.2, mb: 1 }} startIcon={<CloudDownloadIcon />} onClick={handleDescargarPlantillaItems}>
                                    Descargar Plantilla
                                </Button>
                            </Box>
                            <Divider sx={{ my: 2 }} />
                            {/* Bloque 2: Importar desde archivo */}
                            <Box sx={{ border: '2px dashed #ccc', borderRadius: 3, p: 3, background: '#fafbfd' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <CloudUploadIcon color="success" sx={{ fontSize: 30, mr: 1 }} />
                                    <Typography component="span" fontWeight={700} fontSize={18}>2. Importar Plantilla</Typography>
                                </Box>
                                <Typography variant="body2" sx={{ mb: 2 }}>
                                    Selecciona tu archivo Excel completado para importar los items.
                                </Typography>
                                <label htmlFor="excel-upload-items">
                                    <input
                                        id="excel-upload-items"
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".xlsx,.xls"
                                        style={{ display: 'none' }}
                                        onChange={async (e) => {
                                            const ok = await handleImportChange(e);
                                            if (ok) setOpenImportar(false);
                                        }}
                                    />
                                    <Button variant="contained" fullWidth color="success" startIcon={<CloudUploadIcon />} component="span" sx={{ fontWeight: 700, py: 1.2 }}>
                                        Seleccionar Archivo
                                    </Button>
                                </label>
                            </Box>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenImportar(false)} color="inherit">Cancelar</Button>
                    </DialogActions>
                </Dialog>

                {/* Diálogo de búsqueda de productos */}
                <Dialog 
                    open={openProductoSearch} 
                    onClose={() => {
                        setOpenProductoSearch(false);
                        setProductoSearchIndex(null);
                        setProductoSearchTerm('');
                        setProductos([]);
                    }} 
                    maxWidth="md" 
                    fullWidth
                >
                    <DialogTitle>Buscar Producto</DialogTitle>
                    <DialogContent>
                        <Box display="flex" gap={1} mb={2} sx={{ mt: 2 }}>
                            <TextField
                                fullWidth
                                label="Buscar producto"
                                value={productoSearchTerm}
                                onChange={(e) => setProductoSearchTerm(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        handleProductoSearch();
                                    }
                                }}
                            />
                            <Button
                                variant="contained"
                                onClick={handleProductoSearch}
                                disabled={productoSearchLoading}
                            >
                                Buscar
                            </Button>
                        </Box>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Código</TableCell>
                                        <TableCell>Descripción</TableCell>
                                        <TableCell align="center">Acciones</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {productos.length === 0 && !productoSearchLoading && (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center">
                                                {productoSearchTerm ? 'No se encontraron productos' : 'Ingrese un término de búsqueda'}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {productos.map((producto) => (
                                        <TableRow key={producto.codigoArticulo || producto.codigo}>
                                            <TableCell>{producto.codigoArticulo || producto.codigo}</TableCell>
                                            <TableCell>{producto.descripcion || producto.nombreLargo}</TableCell>
                                            <TableCell align="center">
                                                <Button
                                                    variant="outlined"
                                                    onClick={() => handleProductoSelect(producto)}
                                                >
                                                    Seleccionar
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => {
                            setOpenProductoSearch(false);
                            setProductoSearchIndex(null);
                            setProductoSearchTerm('');
                            setProductos([]);
                        }}>Cancelar</Button>
                    </DialogActions>
                </Dialog>

                <Box sx={{ 
                    mt: 3, 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2, 
                    justifyContent: 'flex-end' 
                }}>
                    <Button
                        variant="outlined"
                        onClick={() => navigate('/inventario/transferencias')}
                        fullWidth={isMobile}
                        sx={{
                            borderRadius: 2,
                            px: 4,
                            py: 1.5,
                            textTransform: 'none',
                            fontWeight: 500,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={loading}
                        fullWidth={isMobile}
                        sx={{
                            borderRadius: 2,
                            px: 4,
                            py: 1.5,
                            textTransform: 'none',
                            fontWeight: 500,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        }}
                    >
                        Guardar
                    </Button>
                </Box>
            </form>
            </Card>
        </Box>
    );
};

export default AjusteInventarioForm; 